import { FeatureFlags } from '~constants/featureFlags';
import { CompanySettingKey } from '~hooks/useCompanySettings/models';

type CompanySettingCategory =
  | 'display'
  | 'quickbooks_export'
  | 'quickbooks_invoice_lines'
  | 'settlements';

export interface CompanySettingCatalogEntry {
  category: CompanySettingCategory;
  descriptionKey?: string;
  featureFlag?: FeatureFlags;
  isCheckboxInverted?: boolean;
  labelKey: string;
}

// Opt-in: a setting the api-spec adds stays hidden until it gets an entry here (plus
// the two i18n keys it references), so a spec bump never needs a catalog change.
// `satisfies` still rejects keys the spec no longer has.
export const companySettingsCatalog = {
  apply_rate_changes_default_on: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.apply_rate_changes_default_on.description',
    labelKey:
      'administration.configuration.settings.display.apply_rate_changes_default_on.label',
  },
  auto_assign_driver_equipment_to_jobs: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.auto_assign_driver_equipment_to_jobs.description',
    labelKey:
      'administration.configuration.settings.display.auto_assign_driver_equipment_to_jobs.label',
  },
  chase_missing_tickets_default_on: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.chase_missing_tickets_default_on.description',
    featureFlag: FeatureFlags.driverAgent,
    labelKey:
      'administration.configuration.settings.display.chase_missing_tickets_default_on.label',
  },
  collapse_dispatch_order_cards_by_default: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.collapse_dispatch_order_cards_by_default.description',
    labelKey:
      'administration.configuration.settings.display.collapse_dispatch_order_cards_by_default.label',
  },
  driver_require_all_permissions: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.driver_require_all_permissions.description',
    labelKey:
      'administration.configuration.settings.display.driver_require_all_permissions.label',
  },
  hide_dispatch_orders_equipment_type_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.hide_dispatch_orders_equipment_type_column.description',
    isCheckboxInverted: true,
    labelKey:
      'administration.configuration.settings.display.hide_dispatch_orders_equipment_type_column.label',
  },
  hide_dispatch_orders_materials_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.hide_dispatch_orders_materials_column.description',
    isCheckboxInverted: true,
    labelKey:
      'administration.configuration.settings.display.hide_dispatch_orders_materials_column.label',
  },
  hide_dispatch_orders_pickup_dropoff_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.hide_dispatch_orders_pickup_dropoff_column.description',
    isCheckboxInverted: true,
    labelKey:
      'administration.configuration.settings.display.hide_dispatch_orders_pickup_dropoff_column.label',
  },
  hide_dispatch_orders_start_time_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.hide_dispatch_orders_start_time_column.description',
    isCheckboxInverted: true,
    labelKey:
      'administration.configuration.settings.display.hide_dispatch_orders_start_time_column.label',
  },
  hide_driver_equipment_when_job_has_equipment: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.hide_driver_equipment_when_job_has_equipment.description',
    isCheckboxInverted: true,
    labelKey:
      'administration.configuration.settings.display.hide_driver_equipment_when_job_has_equipment.label',
  },
  hide_project_external_id_in_order_name: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.hide_project_external_id_in_order_name.description',
    labelKey:
      'administration.configuration.settings.display.hide_project_external_id_in_order_name.label',
  },
  itemize_material_rate_default_on: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.itemize_material_rate_default_on.description',
    labelKey:
      'administration.configuration.settings.display.itemize_material_rate_default_on.label',
  },
  accounting_export_populates_invoice_service_date: {
    category: 'quickbooks_invoice_lines',
    descriptionKey:
      'administration.integration.rutter.quick_books_online.advanced_configuration.settings.accounting_export_populates_invoice_service_date.description',
    labelKey:
      'administration.integration.rutter.quick_books_online.advanced_configuration.settings.accounting_export_populates_invoice_service_date.label',
  },
  accounting_platform_assigns_document_number: {
    category: 'quickbooks_export',
    labelKey:
      'administration.integration.rutter.quick_books_online.advanced_configuration.settings.accounting_platform_assigns_document_number.label',
    descriptionKey:
      'administration.integration.rutter.quick_books_online.advanced_configuration.settings.accounting_platform_assigns_document_number.description',
  },
  live_eta_default: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.live_eta_default.description',
    featureFlag: FeatureFlags.liveEtaEnabled,
    labelKey: 'administration.configuration.settings.display.live_eta_default.label',
  },
  require_approval_to_settle: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_approval_to_settle.description',
    featureFlag: FeatureFlags.chargeBasedSettlements,
    labelKey:
      'administration.configuration.settings.display.require_approval_to_settle.label',
  },
  require_department: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_department.description',
    labelKey: 'administration.configuration.settings.display.require_department.label',
  },
  require_material_rate: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_material_rate.description',
    labelKey: 'administration.configuration.settings.display.require_material_rate.label',
  },
  require_order_name: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_order_name.description',
    labelKey: 'administration.configuration.settings.display.require_order_name.label',
  },
  require_order_service_class: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_order_service_class.description',
    labelKey:
      'administration.configuration.settings.display.require_order_service_class.label',
  },
  require_pickup_site: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_pickup_site.description',
    labelKey: 'administration.configuration.settings.display.require_pickup_site.label',
  },
  require_rates_by_department_name: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_rates_by_department_name.description',
    labelKey:
      'administration.configuration.settings.display.require_rates_by_department_name.label',
  },
  require_salesperson: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_salesperson.description',
    labelKey: 'administration.configuration.settings.display.require_salesperson.label',
  },
  require_tax: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.require_tax.description',
    labelKey: 'administration.configuration.settings.display.require_tax.label',
  },
  running_default_on: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.running_default_on.description',
    labelKey: 'administration.configuration.settings.display.running_default_on.label',
  },
  settlement_commission_deduction_line: {
    category: 'settlements',
    descriptionKey:
      'administration.configuration.settings.settlements.settlement_commission_deduction_line.description',
    featureFlag: FeatureFlags.payCommissions,
    labelKey:
      'administration.configuration.settings.settlements.settlement_commission_deduction_line.label',
  },
  share_order_totals: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.share_order_totals.description',
    labelKey: 'administration.configuration.settings.display.share_order_totals.label',
  },
  show_canceled_rejected_orders: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_canceled_rejected_orders.description',
    labelKey:
      'administration.configuration.settings.display.show_canceled_rejected_orders.label',
  },
  show_default_digital_ticket_view: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_default_digital_ticket_view.description',
    labelKey:
      'administration.configuration.settings.display.show_default_digital_ticket_view.label',
  },
  show_contract_number: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_contract_number.description',
    labelKey: 'administration.configuration.settings.display.show_contract_number.label',
  },
  show_dispatch_jobs_driver_labels: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_dispatch_jobs_driver_labels.description',
    labelKey:
      'administration.configuration.settings.display.show_dispatch_jobs_driver_labels.label',
  },
  show_dispatch_jobs_materials_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_dispatch_jobs_materials_column.description',
    labelKey:
      'administration.configuration.settings.display.show_dispatch_jobs_materials_column.label',
  },
  show_dispatch_jobs_pickup_dropoff_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_dispatch_jobs_pickup_dropoff_column.description',
    labelKey:
      'administration.configuration.settings.display.show_dispatch_jobs_pickup_dropoff_column.label',
  },
  show_dispatch_jobs_truck_and_trailer_column: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_dispatch_jobs_truck_and_trailer_column.description',
    labelKey:
      'administration.configuration.settings.display.show_dispatch_jobs_truck_and_trailer_column.label',
  },
  show_driver_trailer: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_driver_trailer.description',
    labelKey: 'administration.configuration.settings.display.show_driver_trailer.label',
  },
  show_service_class: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.show_service_class.description',
    labelKey: 'administration.configuration.settings.display.show_service_class.label',
  },
  sort_dispatch_orders_by_dispatch_number: {
    category: 'display',
    descriptionKey:
      'administration.configuration.settings.display.sort_dispatch_orders_by_dispatch_number.description',
    labelKey:
      'administration.configuration.settings.display.sort_dispatch_orders_by_dispatch_number.label',
  },
} satisfies Partial<Record<CompanySettingKey, CompanySettingCatalogEntry>>;

export type CatalogedCompanySettingKey = keyof typeof companySettingsCatalog;

export type CompanySettingGatingFeatureFlag =
  (typeof companySettingsCatalog)[CatalogedCompanySettingKey] extends infer Entry
    ? Entry extends { featureFlag: infer Flag }
      ? Flag
      : never
    : never;

// Sound cast: the catalog's keys are CatalogedCompanySettingKey by construction
// (Object.keys alone widens to string[]).
export const companySettingKeys = Object.keys(
  companySettingsCatalog,
) as CatalogedCompanySettingKey[];

export const settingKeysInCategory = (
  keys: CatalogedCompanySettingKey[],
  category: CompanySettingCategory,
) => keys.filter((key) => companySettingsCatalog[key].category === category);

export const companySettingCategoryLabelKeys: Record<CompanySettingCategory, string> = {
  display: 'administration.configuration.settings.display.setting_title',
  quickbooks_export:
    'administration.integration.rutter.quick_books_online.advanced_configuration.invoice_bill_numbers',
  quickbooks_invoice_lines:
    'administration.integration.rutter.quick_books_online.advanced_configuration.service_date',
  settlements: 'administration.configuration.settings.settlements.setting_title',
};

// Categories rendered on Settings → Configuration. The others surface on the page
// they belong to.
export const configurationPageCategories: CompanySettingCategory[] = [
  'display',
  'settlements',
];

export const quickBooksPageCategories: CompanySettingCategory[] = [
  'quickbooks_export',
  'quickbooks_invoice_lines',
];
